﻿<template>
  <div class="booking-table-wrapper">
    <div class="table-toolbar" v-if="(bookings?.length ?? 0) > 0">
      <span v-if="Active_Filter_Count > 0">
        Showing <strong>{{ Sorted_Bookings.length }}</strong> of <strong>{{ bookings?.length ?? 0 }}</strong> bookings
      </span>
      <span v-else>
        <strong>{{ bookings?.length ?? 0 }}</strong> {{ (bookings?.length ?? 0) === 1 ? 'booking' : 'bookings' }} · click a column title to filter
      </span>
      <button v-if="Active_Filter_Count > 0" type="button" class="mb-link-btn" @click="Clear_All_Filters()">
        Clear {{ Active_Filter_Count === 1 ? 'filter' : 'filters' }}
      </button>
    </div>
    <div class="booking-table">
      <div class="table-responsive">
        <table class="table">
          <thead>
            <tr>
              <th v-for="col in columns" :key="col.key" class="filter-th" :class="{ 'filter-open': Is_Filter_Open(col.key), 'col-optional': col.optional, 'is-date': Is_Date_Key(col.key) }" @click="Open_Filter(col.key, $event)">
                <div class="th-inner">
                  <button v-if="col.key === 'trip'" type="button" class="expander-btn" @click.stop="Toggle_Names_All()" :aria-pressed="namesExpandedAll" :title="namesExpandedAll ? 'Shorten trip names' : 'Show full trip names'">
                    <span v-if="namesExpandedAll">[-]</span><span v-else>[+]</span>
                  </button>
                  <div class="filter-field">
                    <span class="filter-label">
                      {{ col.label }}
                      <svg v-if="Is_Date_Key(col.key)" class="filter-date-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>
                    </span>
                    <!-- Departure / Booked: a date filter (popover below); the chosen dates show here -->
                    <template v-if="Is_Date_Key(col.key)">
                      <span v-if="Date_Filter_Active(col.key)" class="filter-date-value" :title="Date_Filter_Summary(col.key)">{{ Date_Filter_Summary(col.key) }}</span>
                      <button v-if="Date_Filter_Active(col.key)" type="button" class="filter-clear" title="Clear filter" @mousedown.prevent @click.stop="Clear_Filter(col.key)">×</button>
                    </template>
                    <!-- Consultant / Status: a dropdown of the values in the column -->
                    <select
                      v-else-if="Is_Select_Key(col.key)"
                      v-show="Is_Filter_Open(col.key)"
                      v-model="filters[col.key]"
                      :data-filter="col.key"
                      :aria-label="'Filter ' + col.label"
                      class="filter-input filter-select"
                      :class="{ 'has-value': filters[col.key] !== '' }"
                      @click.stop
                      @change="Close_Filter(col.key)"
                      @blur="Close_Filter(col.key)"
                      @keydown.esc="Clear_Filter(col.key)"
                    >
                      <option value="">All</option>
                      <option v-for="opt in Select_Options(col.key)" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                    </select>
                    <input
                      v-else
                      v-show="Is_Filter_Open(col.key)"
                      v-model="filters[col.key]"
                      :data-filter="col.key"
                      :aria-label="'Filter ' + col.label"
                      type="text"
                      class="filter-input"
                      @click.stop
                      @blur="Close_Filter(col.key)"
                      @keydown.esc="Clear_Filter(col.key)"
                    />
                    <button v-if="!Is_Date_Key(col.key) && !Is_Select_Key(col.key) && filters[col.key]" type="button" class="filter-clear" title="Clear filter" @mousedown.prevent @click.stop="Clear_Filter(col.key)">×</button>
                  </div>
                  <button type="button" class="sort-btn" :class="{ 'sort-active': sortKey === col.key }" :title="'Sort by ' + col.label" @click.stop="setSort(col.key)">
                    {{ sortKey === col.key ? (sortAsc ? '▲' : '▼') : '⇅' }}
                  </button>
                </div>
              </th>
              <th v-if="(bookings?.length ?? 0) > 0" class="text-center"><span class="sr-only">View</span></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(b, idx) in Sorted_Bookings" :key="Row_Key(b, idx)">
              <td class="trip-click cell-strong cell-wrap" :title="namesExpandedAll ? 'Click to collapse' : Trip_Name(b)" @click="Toggle_Names_All()">
                {{ Display_Trip_Name(b) }}
              </td>
              <td>{{ Departure_Date_Display(b) }}</td>
              <td class="cell-strong">{{ Booking_ID(b) }}</td>
              <td class="cell-muted cell-wrap">{{ Agent_Reference(b) || '—' }}</td>
              <td class="cell-wrap">{{ Lead_Passenger(b) }}</td>
              <td class="cell-muted col-optional">{{ Booking_Date_Display(b) }}</td>
              <td class="cell-muted cell-wrap col-optional">{{ Get_Consultant(b) || '—' }}</td>
              <td>
                <span v-if="Booking_Status(b)" class="mb-pill" :class="Status_Class(b)">{{ Booking_Status(b) }}</span>
                <span v-else class="cell-muted">—</span>
              </td>
              <td class="text-center">
                <button
                  type="button"
                  class="mb-icon-btn"
                  :class="{ 'is-loading': idx === loadingIndex, 'is-dimmed': loadingIndex !== -1 && idx !== loadingIndex }"
                  :disabled="loadingIndex !== -1"
                  :title="idx === loadingIndex ? 'Loading booking…' : 'View booking details'"
                  :aria-label="idx === loadingIndex ? 'Loading booking' : 'View booking details'"
                  @click="Booking_Details(b, idx)"
                >
                  <!-- spinner while the booking loads -->
                  <svg v-if="idx === loadingIndex" class="mb-spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" opacity="0.25" />
                    <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" stroke-width="3" stroke-linecap="round" />
                  </svg>
                  <!-- normal icon -->
                  <svg v-else xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                </button>
              </td>
            </tr>
            <tr v-if="(bookings?.length ?? 0) === 0">
              <td colspan="8">
                <div class="mb-empty">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <rect x="3" y="4" width="18" height="17" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" />
                  </svg>
                  <strong>No bookings yet</strong>
                  <span>Bookings made for this account will appear here.</span>
                </div>
              </td>
            </tr>
            <tr v-else-if="Sorted_Bookings.length === 0">
              <td colspan="9">
                <div class="mb-empty">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
                  </svg>
                  <strong>No bookings match the current filters</strong>
                  <button type="button" class="mb-link-btn" @click="Clear_All_Filters()">Clear filters</button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <details_dialog v-if="detailsOpen" @close="Close_Details" />

    <!-- Departure / Booked date filter: a single day, a range or a month. Teleported and fixed under the column header,
         because the table scrolls sideways and would clip it. -->
    <Teleport to="body">
      <div v-if="datePopover" class="mb-date-pop" role="dialog" :aria-label="'Filter by ' + Column_Label(datePopover)"
           :style="{ top: datePopoverPos.top + 'px', left: datePopoverPos.left + 'px' }"
           @keydown.esc="Close_Date_Popover()">
        <div class="mb-date-pop-head">
          <strong>{{ datePopover === 'departure' ? 'Departure date' : 'Booking date' }}</strong>
          <button type="button" class="mb-date-pop-close" aria-label="Close" @click="Close_Date_Popover()">×</button>
        </div>

        <div class="mb-date-modes" role="radiogroup" aria-label="Filter type">
          <button v-for="m in [{ value: 'day', label: 'Day' }, { value: 'range', label: 'Range' }, { value: 'month', label: 'Month' }]" :key="m.value"
                  type="button" role="radio" :aria-checked="dateFilters[datePopover].mode === m.value"
                  :class="{ 'is-active': dateFilters[datePopover].mode === m.value }"
                  @click="Set_Date_Mode(datePopover, m.value)">{{ m.label }}</button>
        </div>

        <div v-if="dateFilters[datePopover].mode === 'day'" class="mb-date-fields">
          <label class="mb-date-field">
            <span>Date</span>
            <input type="date" class="mb-input" v-model="dateFilters[datePopover].day" @click="Open_Picker" />
          </label>
        </div>

        <div v-else-if="dateFilters[datePopover].mode === 'range'" class="mb-date-fields is-two">
          <label class="mb-date-field">
            <span>From</span>
            <input type="date" class="mb-input" v-model="dateFilters[datePopover].from" :max="dateFilters[datePopover].to || undefined" @click="Open_Picker" />
          </label>
          <label class="mb-date-field">
            <span>To</span>
            <input type="date" class="mb-input" v-model="dateFilters[datePopover].to" :min="dateFilters[datePopover].from || undefined" @click="Open_Picker" />
          </label>
          <p class="mb-date-hint">Leave one end empty for an open range.</p>
        </div>

        <div v-else class="mb-date-fields">
          <label class="mb-date-field">
            <span>Month</span>
            <select class="mb-input" v-model="dateFilters[datePopover].month">
              <option value="">Choose a month</option>
              <option v-for="m in Date_Months(datePopover)" :key="m.value" :value="m.value">{{ m.label }}</option>
            </select>
          </label>
        </div>

        <div class="mb-date-quick">
          <button v-for="q in Date_Quick_Picks(datePopover)" :key="q.label" type="button" @click="q.apply()">{{ q.label }}</button>
        </div>

        <div class="mb-date-pop-foot">
          <button type="button" class="mb-link-btn" :disabled="!Date_Filter_Active(datePopover)" @click="Clear_Date_Filter(datePopover)">Clear</button>
          <button type="button" class="mb-date-done" @click="Close_Date_Popover()">Done</button>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts" src="./booking_table.ts"></script>
